import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import OutlineDisplay from './OutlineDisplay';
import { useState, useEffect } from 'react';
import useGraphData from './useGraphData';
import './base.css'

const isValidDateFormat = (pageName) => {
  const dateRegex = /^[A-Z][a-z]{2} \d{1,2}(st|nd|rd|th), \d{4}$/;
  return dateRegex.test(pageName);
};

const processData = (rawData) => {
  const idToBlockMap = new Map();
  const idToReferencesMap = new Map();

  const processBlock = (block, parentId = null, section = null) => {

    // Add parent reference to the block
    block.parentId = parentId;

    block.properties = block.properties || {};

    if (block['page-name']){
      block.id = decodeURIComponent(
        block['page-name']
          .replace(/\s+/g, '-') // Replace spaces with dashes
          .replace(/[^\w-]/g, '') // Remove punctuation
      );
      block.content = block['page-name'];
      if (isValidDateFormat(block['page-name'])) {
        block.properties.type = 'journal';
      }else{
        block.properties.type = 'page';
      }
    }

    // Parse properties from content
    const { content, properties } = parseProperties(block.content || '');
    block.content = content;
    block.properties = { ...block.properties, ...properties };

    if (block.properties.type === 'section') {
      section = block.id;
    }

    if (section) {
      block.section = section;
    }


    // If the block has properties but no content, apply the properties to the parent
    if (!block.content && Object.keys(block.properties).length > 0 && parentId) {
      const parentBlock = idToBlockMap.get(parentId);
      if (parentBlock) {
        parentBlock.properties = { ...parentBlock.properties, ...block.properties };
      }
      // also remove the node from the parent's children
      if (parentBlock.children) {
        parentBlock.children = parentBlock.children.filter(child => child.id !== block.id);
      }


      return;
    }

    // Add block to idToBlockMap
    idToBlockMap.set(block.id, block);

    // Process references in content
    const references = findReferences(content);
    references.forEach(refId => {
      if (!idToReferencesMap.has(refId)) {
        idToReferencesMap.set(refId, new Set());
      }
      idToReferencesMap.get(refId).add(block.id);
    });

    // Recursively process children
    if (block.children && Array.isArray(block.children)) {
      block.children.forEach(child => {
        const exists = idToBlockMap.get(child.id);
        if (!exists) {
          processBlock(child, block.id, section);
        }
      });
    }
  };

  // Start processing from the top-level blocks
  rawData.blocks.forEach(block => processBlock(block));

  return { idToBlockMap, idToReferencesMap };
};

const parseProperties = (content) => {
  const lines = content.split('\n');
  const properties = {};
  const contentLines = [];

  lines.forEach(line => {
    const match = line.match(/^([^:]+)::(.*)$/);
    if (match) {
      const [, key, value] = match;
      const trimmedKey = key.trim();
      if (trimmedKey) {
        properties[trimmedKey] = value.trim();
      }
    } else {
      contentLines.push(line);
    }
  });

  return {
    content: contentLines.join('\n'),
    properties
  };
};

const findReferences = (content) => {
  const regex = /\(\((.*?)\)\)/g;
  const matches = [];
  let match;
  while ((match = regex.exec(content)) !== null) {
    matches.push(match[1]);
  }
  return matches;
};

function App() {
  const [processedData, setProcessedData] = useState(null);

  const { graphData, isLoading, error } = useGraphData();

  useEffect(() => {
    if (!graphData) return;
    const processed = processData(graphData);
    setProcessedData(processed);
  }, [graphData]);

  if (!processedData) {
    return <div>Loading...</div>;
  }

  return (
    <Router basename="/graph">
      <Routes>
        <Route path="/" element={<OutlineDisplay data={graphData} processedData={processedData}  />} />
        <Route path="/block/:blockId" element={<OutlineDisplay data={graphData} processedData={processedData}  />} />
      </Routes>
    </Router>
  );
}

export default App;